
import Vue from "vue";
import Component from "vue-class-component";
import {BModal} from "bootstrap-vue";
import {Prop} from "vue-property-decorator";
import { CountryLightModel, factory, ResultError, ShipInfoModel, ShipTypeLocalizedModel, ShipTypesClient } from "@/api-client";
import DropdownSelector from "@/components/DropdownSelector.vue";
import CheckboxInput from "@/features/shared/components/CheckboxInput.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import toasts from "@/components/Toasts";
import { debounceFn } from "debounce-decorator-ts";
import DropdownSearch from "@/components/DropdownSearch.vue";

@Component({
    components: {
        DropdownSelector,
        CheckboxInput,
        LabelOptional,
        DropdownSearch
    }
})
export default class ShipInfoModal extends Vue {
    @Prop({default: () => []})
    typeOptions!: ShipTypeLocalizedModel[];

    @Prop({default: () => []})
    countries!: CountryLightModel[];

    @Prop()
    existingShip?: ShipInfoModel | null;
    
    @Prop({default: false})
    isInlandVessel!: boolean;
    
    @Prop({default: false})
    isPilotagePractice!: boolean;

    model: ShipInfoModel = factory.create(ShipInfoModel);
    validationErrors: ResultError = factory.create(ResultError);

    $refs!: { bModal: BModal };
    searching = false;
    selectedType: string | undefined;

    created() {
        // Manually watching prop because modal $show gets handled before prop value update is detected
        this.$watch(
            () => this.existingShip,
            () => this.reset()
        );
    }

    mounted() {
        this.$refs.bModal.$on("show", () => this.reset());
    }

    private save() {
        this.validateModel();

        if (this.validationErrors.isEmpty) {
            this.$emit("save", this.model);
            this.hideModal();
            toasts.success(this.$ls.Global_Toast_AddSuccess);
        }
        else {
            this.setValidationErrors(this.validationErrors, this.$ls.Global_Toast_FormAddFailure);
        }
    }

    async searchShipTypes(searchTerm: string) {
        this.searching = true;
        await this.searchShipsDebounced(searchTerm);
    }

    @debounceFn(500, "searchShipsFn")
    async searchShipsDebounced(searchTerm: string) {
        const queryResult = await new ShipTypesClient().shipTypesLocalized(searchTerm);
        this.typeOptions = queryResult.shipTypesLocalized;
        this.searching = false;
    }

    private hideModal() {
        this.$refs.bModal.hide();
    }

    private reset() {
        this.validationErrors.clear();

        if (this.existingShip) {
            this.model = new ShipInfoModel(this.existingShip);
            this.selectedType = this.model.type?.id;
        } else {
            this.model = factory.create(ShipInfoModel);
            // Ensure withImoNr is initialized to false for new ships
            this.model.withImoNr = false;
            this.selectedType = this.model.type?.id;
            if (this.typeOptions.length === 0)
                this.searchShipTypes("");
        }
    }

    getDescription(shipType: ShipTypeLocalizedModel) {
        return `${shipType.name}`;
    }

    get noResultText() {
        return this.searching ? this.$ls.Global_Placeholder_Searching : this.$ls.Documents_Practices_Search_Placeholder_NoShipsFound
    }

    private validateModel() {
        this.validationErrors.clear();

        if (!this.model.name)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddShip_Error_NameIsMandatory, 'name');

        if (this.model.withImoNr && !this.model.imoNr)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddShip_Error_EnterImoNumber, 'imoNr');

        if (!this.model.type)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddShip_Error_ShipTypeIsMandatory, 'type');

        if (!this.model.grossTonnage || this.model.grossTonnage <= 0)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddShip_Error_GrossTonnageMustBeGreaterThan0, 'grossTonnage');

        if (!this.model.mainPower || this.model.mainPower <= 0)
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddShip_Error_MainPowerMustBeGreaterThan0, 'mainPower');
        
        // Validate ship length for inland vessel and pilotage practices
        if ((this.isInlandVessel || this.isPilotagePractice) && (!this.model.lengthOverall || this.model.lengthOverall <= 0))
            this.validationErrors.appendMessage(this.$ls.Documents_Practices_Modal_AddShip_Error_LengthOverallIsMandatory || 'Ship length is mandatory', 'lengthOverall');
    }
}
